/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { createContext, useCallback, useState, useContext, useEffect } from 'react';
import useEscrowAnalysisCalculator from '../common/hooks/useEscrowAnalysisCalculator';
import {
  formatAsDatabaseDate,
  formatDateYYYYMMDD,
  getDateValue,
  getEpochAsYYYYMMDD,
  getErrorFromEndpoint,
  getTimestampAsDate,
  isObjEmptyOrNull,
  isStrEmptyOrNull,
} from '../common/UtilityFunctions';
import dayjs from 'dayjs';
import { useLoanContext } from './LoanContext';
import useGetEventDate from '../common/hooks/useGetEventDate';
import useGetPaymentInfo from '../common/hooks/useGetPaymentInfo';

type EscrowAnalysisCalculationContextType = {
  escrowAnalysisCalculation: UnknownEscrowAnalysisCalculationResp;
  escrowCalcLoading: boolean;
  effectiveDate: dayjs.Dayjs;
  escrowCushionMonths: number;
  shortageMonths: number;
  effectiveDateError: string;
  setEffectiveDate: (val: dayjs.Dayjs) => void;
  setEscrowCushionMonths: (val: number) => void;
  setEffectiveDateError: (val: string) => void;
  setShortageMonths: (val: number) => void;
  escrowCalcErrors: string;
  escrowAnalysisCalculated: boolean;
  setEscrowAnalysisCalculated: (val: boolean) => void;
  handleCalculate: () => Promise<void>;
  effdatestr: string;
  getEscrowAnalysisNumbers: () => any;
  showEscrowNumbersTable: boolean;
  showEscrowMonthsTable: boolean;
  loadingDefaultValues: boolean;
};

const EscrowAnalysisCalculationContext = createContext<EscrowAnalysisCalculationContextType>({
  //showEscrowMonthsTable: false,
  escrowAnalysisCalculation: null,
  escrowCalcLoading: false,
  effectiveDate: null,
  escrowCushionMonths: 0,
  shortageMonths: 12,
  effectiveDateError: null,
  setEffectiveDate: (val: dayjs.Dayjs) => {},
  setEscrowCushionMonths: (val: number) => {},
  setEffectiveDateError: (val: string) => {},
  setShortageMonths: (val: number) => {},
  escrowCalcErrors: '',
  escrowAnalysisCalculated: false,
  setEscrowAnalysisCalculated: (val: boolean) => {},
  handleCalculate: async () => {},
  effdatestr: '',
  getEscrowAnalysisNumbers: () => null,
  showEscrowNumbersTable: false,
  showEscrowMonthsTable: false,
  loadingDefaultValues: false,
});

const useEscrowAnalysisCalculationContext = () => {
  const escrowAnalysisCalculationContext = useContext(EscrowAnalysisCalculationContext);
  if (escrowAnalysisCalculationContext === undefined) {
    /// throwing this error helps in development when the context is out of scope.
    throw new Error('escrowAnalysisCalculationContext must be inside a EscrowAnalysisCalculationProvider');
  }
  return escrowAnalysisCalculationContext;
};

export function earliestPossibleEffectiveDate() {
  let earliestPossible = new Date();
  if (earliestPossible.getDate() > 1) {
    earliestPossible.setMonth(earliestPossible.getMonth() + 1);
  }
  return earliestPossible;
}

export function getEffectiveDateAsDatabaseStr(effectiveDate) {
  if (effectiveDateValid(effectiveDate)) {
    return formatAsDatabaseDate(
      effectiveDate.$d.getFullYear(),
      effectiveDate.$d.getMonth() + 1,
      effectiveDate.$d.getDate(),
    );
  }
  return '';
}

export function effectiveDateValid(effectiveDate) {
  if (isObjEmptyOrNull(effectiveDate)) {
    return false;
  }
  if (!('$d' in effectiveDate)) {
    return false;
  }
  return true;
}

export function getEffectiveDateUTCTimestamp(effectiveDate) {
  if (effectiveDateValid(effectiveDate)) {
    const datestr = formatDateYYYYMMDD(effectiveDate.$d);
    const utctimestamp = getDateValue(datestr);
    return utctimestamp;
  }
  return '';
}

const EscrowAnalysisCalculationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [effectiveDateError, setEffectiveDateError] = useState(null);
  const [effectiveDate, setEffectiveDate] = useState(null);
  const [escrowCushionMonths, setEscrowCushionMonths] = useState(0);
  const [shortageMonths, setShortageMonths] = useState(12);
  const [loadingDefaultValues, setLoadingDefaultValues] = useState(true);
  const [escrowAnalysisCalculated, setEscrowAnalysisCalculated] = useState(false);
  const { escrowAnalysisCalculation, escrowCalcLoading, escrowCalcErrors, getEscrowAnalysisCalculation } =
    useEscrowAnalysisCalculator();

  const { loanNumber } = useLoanContext();
  const { getEventDateInfo } = useGetEventDate();
  const { loadPayment } = useGetPaymentInfo(loanNumber);
  const effdatestr = getEffectiveDateAsDatabaseStr(effectiveDate);
  const showEscrowMonthsTable =
    escrowAnalysisCalculated &&
    !isObjEmptyOrNull(escrowAnalysisCalculation?.trialBalances) &&
    isStrEmptyOrNull(escrowCalcErrors) &&
    !escrowCalcLoading;
  const showEscrowNumbersTable =
    escrowAnalysisCalculated &&
    !isObjEmptyOrNull(escrowAnalysisCalculation) &&
    isStrEmptyOrNull(escrowCalcErrors) &&
    !escrowCalcLoading;

  const handleCalculate = useCallback(async () => {
    const effectiveDateUTCTimestamp = getEffectiveDateUTCTimestamp(effectiveDate);
    if (!isStrEmptyOrNull(loanNumber) && effectiveDateUTCTimestamp !== '') {
      const params = {
        aeaEffectiveDate: effectiveDateUTCTimestamp,
        tiCushionMonths: escrowCushionMonths,
        repayMonths: shortageMonths,
      };
      const res = await getEscrowAnalysisCalculation(params);
      setEscrowAnalysisCalculated(isObjEmptyOrNull(res) ? false : true);
    }
  }, [loanNumber, escrowCushionMonths, shortageMonths, getEscrowAnalysisCalculation, effectiveDate]);

  const getDefaultCushionMonths = useCallback(async () => {
    const aeaEvent = await getEventDateInfo(loanNumber, 'AEA', '00');
    if (isObjEmptyOrNull(aeaEvent)) {
      return 0;
    }
    if (!isStrEmptyOrNull(getErrorFromEndpoint(aeaEvent))) {
      return 0;
    }
    if (aeaEvent.hasOwnProperty('active') && aeaEvent.active == false) {
      return 0;
    }
    const aeaDate = getEpochAsYYYYMMDD(aeaEvent.activeDate);
    const payment = await loadPayment(aeaDate);
    if (isObjEmptyOrNull(payment)) {
      return 0;
    }
    if (!isStrEmptyOrNull(getErrorFromEndpoint(payment))) {
      return 0;
    }
    if (payment.hasOwnProperty('escrowAnalysisTiCushion') && payment.escrowAnalysisTiCushion != null) {
      return payment.escrowAnalysisTiCushion;
    }
    return 0;
  }, [loanNumber, getEventDateInfo, loadPayment]);

  useEffect(() => {
    async function getDefaultVals() {
      let cushionMonths = await getDefaultCushionMonths();
      setEscrowCushionMonths(cushionMonths);
      setLoadingDefaultValues(false);
    }
    getDefaultVals();
  }, [getDefaultCushionMonths]);

  const getEscrowAnalysisNumbers = useCallback(() => {
    if (showEscrowNumbersTable) {
      let escrowAnalysisNumbers = {
        effectiveDate: getTimestampAsDate(escrowAnalysisCalculation.aeaEffectiveDate),
        currentEscrowBalance: parseFloat(escrowAnalysisCalculation.currentBalance).toFixed(2),
        estimatedBalance: parseFloat(escrowAnalysisCalculation.estimatedBalance).toFixed(2),
        projectedBalance: parseFloat(escrowAnalysisCalculation.projectedBalance).toFixed(2),
        shortSurplus: parseFloat(escrowAnalysisCalculation.escrowShortSurplus).toFixed(2),
        prinInt: escrowAnalysisCalculation.monthlyPI.toFixed(2),
        newEscrowPayment: parseFloat(escrowAnalysisCalculation.newEscrowPayment).toFixed(2),
        newMonthlyPayment: parseFloat(escrowAnalysisCalculation.newMonthlyPayment).toFixed(2),
        newEscrowPaymentWOShortSurplus: parseFloat(escrowAnalysisCalculation.expectedEscrowPayment).toFixed(2),
        newMonthlyPaymentWOShortSurplus: parseFloat(escrowAnalysisCalculation.expectedMonthlyPayment).toFixed(2),
        paymentChange: parseFloat(escrowAnalysisCalculation.paymentChange).toFixed(2),
        refund: parseFloat(escrowAnalysisCalculation.intendedRefund).toFixed(2),
        refundNotApplicable: escrowAnalysisCalculation.refundNotApplicable,
        escrowShortageRepayMonths: shortageMonths,
        pitiDiff: parseFloat(escrowAnalysisCalculation.pitiDiff).toFixed(2),
        newMonthlyImpound: parseFloat(escrowAnalysisCalculation.newMonthlyImpound).toFixed(2),
      };
      return escrowAnalysisNumbers;
    }
    return null;
  }, [escrowAnalysisCalculation, showEscrowNumbersTable, shortageMonths]);

  return (
    <EscrowAnalysisCalculationContext.Provider
      value={{
        escrowAnalysisCalculation,
        effectiveDate,
        escrowCushionMonths,
        shortageMonths,
        escrowAnalysisCalculated,
        effectiveDateError,
        setEffectiveDate,
        setEscrowCushionMonths,
        setEffectiveDateError,
        setShortageMonths,
        escrowCalcErrors,
        escrowCalcLoading,
        setEscrowAnalysisCalculated,
        handleCalculate,
        effdatestr,
        getEscrowAnalysisNumbers,
        showEscrowNumbersTable,
        showEscrowMonthsTable,
        loadingDefaultValues,
      }}
    >
      {children}
    </EscrowAnalysisCalculationContext.Provider>
  );
};

export { EscrowAnalysisCalculationProvider, useEscrowAnalysisCalculationContext };
